import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

import { useSiteConfigStore } from './siteConfig'

const MAX_TOAST_SIZE = 30

export const useMainStore = defineStore('main', () => {
  // 延遲取得 siteConfigStore，避免在 Pinia 初始化前調用
  const getSiteConfigStore = () => useSiteConfigStore()

  // State (使用 ref)
  const loading = ref({})
  const downloadGuide = ref(false)
  const modalLoading = ref(false)
  const loadingGame = ref(false)
  const modal = ref(null)
  const modalParams = ref(null)
  const systemModal = ref(null)
  const systemModalParams = ref(null)
  const verifyModal = ref(null)
  const bindBankStrategy = ref(null)
  const bindBankCheckpoint = ref(null)
  const showBindBank = ref(false)
  const bindBankModal = ref(null)

  // Promise resolver（用於等待銀行綁定完成）
  let bindBankResolver = null
  const errorMessage = ref(null)
  const errorModal = ref(null)
  const animationInitData = ref(null)

  // Global info
  const jackpotList = ref([])
  const turnoverLimitTip = ref(false)
  const withdrawRule = ref({})
  const promotion = ref(null)
  const browserAgent = ref(null)
  const navigator = ref({})
  const trackLocation = ref(null)
  const storage = ref(null)
  const customerServiceModal = ref(null)
  const playingGame = ref(null)
  const jackpotWinning = ref({})
  const toast = ref([])
  const gameSearchPageEnable = ref(false)
  const connectSocket = ref(0)
  const providerInfo = ref([])
  const freeGameList = ref([])
  const telegramUserData = ref(null)
  const updateMessage = ref(false)
  const normalBroadcast = ref(null)
  const shareGame = ref(null)

  // Getters (只保留有計算邏輯的 computed，冗餘的 getter 改為直接引用 ref)
  const isWebView = computed(() => {
    return browserAgent.value?.ua?.includes('th1games')
  })

  const isAndroid = computed(() => {
    return navigator.value.userAgent?.indexOf('Android') > -1 || navigator.value.userAgent?.indexOf('Adr') > -1
  })

  const isIOS = computed(() => {
    return browserAgent.value?.os?.name === 'iOS'
  })

  const isInStandaloneMode = computed(() => {
    return 'standalone' in navigator.value && navigator.value.standalone
  })

  const downloadVisible = computed(() => {
    return !isWebView.value && !isInStandaloneMode.value && (isAndroid.value || isIOS.value)
  })

  const isTablet = computed(() => {
    const userAgent = navigator.value.userAgent.toLowerCase()
    const isAndroidTablet = /android/.test(userAgent) && !/mobile/.test(userAgent)
    const isIPad = /ipad/.test(userAgent)
    return isAndroidTablet || isIPad
  })

  const isDesktop = computed(() => {
    return !isWebView.value && !isInStandaloneMode.value && !isAndroid.value && !isIOS.value
  })

  const iOSversion = computed(() => {
    return browserAgent.value?.os?.version
  })

  const getDownloadLink = computed(() => {
    const parameterObj = {
      ac: storage.value?.get('ac'),
      fc: storage.value?.get('fc'),
      pix: storage.value?.get('pix'),
      kpix: storage.value?.get('kpix'),
    }
    return getSiteConfigStore().getDownloadLink(parameterObj)
  })

  const getLoading = computed(() => {
    return Object.values(loading.value).some((v) => v)
  })

  const getJackpotList = computed(() => jackpotList.value || [])

  const isBRLCurrency = computed(() => {
    return (process.env.CURRENCY_SYMBOL || '') === 'R$'
  })

  const getConnectSocket = computed(() => {
    return connectSocket.value === 2
  })

  // Actions (使用普通函數)
  function setFreeGameList(data) {
    freeGameList.value = data
  }

  function setProviderInfo(data) {
    providerInfo.value = data
  }

  function setDownloadGuide(data) {
    downloadGuide.value = data
  }

  function setLoading(data) {
    loading.value = { ...loading.value, ...data }
  }

  function setModalLoading(data) {
    modalLoading.value = data
  }

  function setLoadingGame(data) {
    loadingGame.value = data
  }

  function setModal(data) {
    modal.value = data
    if (data === null || data === undefined) {
      modalParams.value = null
    }
  }

  function setModalParams(data) {
    modalParams.value = data
  }

  function setShareGame(data) {
    shareGame.value = data
  }

  function setSystemModal(data) {
    systemModal.value = data
    if (data === null || data === undefined) {
      systemModalParams.value = null
    }
  }

  function setSystemModalParams(data) {
    systemModalParams.value = data
  }

  function closeModal(data) {
    if (data === modal.value) {
      modal.value = null
    }
  }

  function setVerifyModal(data) {
    verifyModal.value = data
  }

  function setBindBankStrategy(data) {
    bindBankStrategy.value = data
  }

  function setBindBankCheckpoint(data) {
    bindBankCheckpoint.value = data
  }

  function setShowBindBank(data) {
    showBindBank.value = data
  }

  function setBindBankModal(data) {
    bindBankModal.value = data
  }

  /**
   * 設定銀行綁定 Promise resolver
   */
  function setBindBankResolver(resolver) {
    bindBankResolver = resolver
  }

  /**
   * 解決銀行綁定 Promise
   * @param {boolean} success - 綁定是否成功
   */
  function resolveBindBank(success) {
    if (bindBankResolver) {
      bindBankResolver(success)
      bindBankResolver = null
    }
  }

  function setErrorMessage(data) {
    errorMessage.value = data
  }

  function setErrorModal(data) {
    errorModal.value = data
  }

  function setAnimationInitData(data) {
    animationInitData.value = data
  }

  function setJackpotList(data) {
    jackpotList.value = data
  }

  const setTurnoverLimitTip = (data) => {
    turnoverLimitTip.value = data
  }

  function setWithdrawRule(data) {
    withdrawRule.value = data
  }

  function setPromotion(data) {
    promotion.value = data
  }

  function setBrowserAgent(data) {
    browserAgent.value = data
  }

  function setNavigator(data) {
    navigator.value = data
  }

  function setTrackLocation(data) {
    trackLocation.value = data
  }

  function setStorage(data) {
    storage.value = data
  }

  function setCustomrerServiceModal(data) {
    customerServiceModal.value = data
  }

  function setPlayingGame(data) {
    playingGame.value = data
  }

  function setJackpotWinning(data) {
    jackpotWinning.value = data
  }

  function setToast(data) {
    toast.value = data
  }

  function pushToast(data) {
    toast.value = [...toast.value, data]
    if (toast.value.length > MAX_TOAST_SIZE) {
      toast.value = toast.value.slice(-MAX_TOAST_SIZE)
    }
  }

  function setGameSearchPageEnable(data) {
    gameSearchPageEnable.value = data
  }

  function setConnectSocket(data) {
    connectSocket.value = data
  }

  function setTelegramUserData(data) {
    telegramUserData.value = data
  }

  function setUpdateMessage(data) {
    updateMessage.value = data
  }

  function setNormalBroadcast(data) {
    normalBroadcast.value = data
  }

  // Return everything to make it available
  return {
    // State
    loading,
    downloadGuide,
    modalLoading,
    loadingGame,
    modal,
    modalParams,
    systemModal,
    systemModalParams,
    verifyModal,
    bindBankStrategy,
    bindBankCheckpoint,
    showBindBank,
    bindBankModal,
    errorMessage,
    errorModal,
    animationInitData,
    jackpotList,
    turnoverLimitTip,
    withdrawRule,
    promotion,
    browserAgent,
    navigator,
    trackLocation,
    storage,
    customerServiceModal,
    playingGame,
    jackpotWinning,
    toast,
    gameSearchPageEnable,
    connectSocket,
    providerInfo,
    freeGameList,
    telegramUserData,
    updateMessage,
    normalBroadcast,
    shareGame,

    // Getters (有計算邏輯的 computed)
    isWebView,
    isAndroid,
    isIOS,
    isInStandaloneMode,
    downloadVisible,
    isTablet,
    isDesktop,
    iOSversion,
    getDownloadLink,
    getLoading,
    getJackpotList,
    isBRLCurrency,
    getConnectSocket,

    // Getter aliases (直接引用 ref，向後相容)
    getFreeGameList: freeGameList,
    getProviderInfo: providerInfo,
    getModalLoading: modalLoading,
    getLoadingGame: loadingGame,
    getModal: modal,
    getModalParams: modalParams,
    getShareGame: shareGame,
    getSystemModal: systemModal,
    getSystemModalParams: systemModalParams,
    getVerifyModal: verifyModal,
    getBindBankStrategy: bindBankStrategy,
    getBindBankCheckpoint: bindBankCheckpoint,
    getShowBindBank: showBindBank,
    getBindBankModal: bindBankModal,
    getErrorMessage: errorMessage,
    getErrorModal: errorModal,
    getAnimationInitData: animationInitData,
    getTrackLocation: trackLocation,
    isGuiding: downloadGuide,
    getTurnoverLimitTip: turnoverLimitTip,
    getWithdrawRule: withdrawRule,
    getPromotion: promotion,
    getCustomerServiceModal: customerServiceModal,
    getPlayingGame: playingGame,
    getJackpotWinning: jackpotWinning,
    getToast: toast,
    getGameSearchPageEnable: gameSearchPageEnable,
    getTelegramUserData: telegramUserData,
    getUpdateMessage: updateMessage,
    getNormalBroadcast: normalBroadcast,

    // Actions
    setFreeGameList,
    setProviderInfo,
    setDownloadGuide,
    setLoading,
    setModalLoading,
    setLoadingGame,
    setModal,
    setModalParams,
    setShareGame,
    setSystemModal,
    setSystemModalParams,
    closeModal,
    setVerifyModal,
    setBindBankStrategy,
    setBindBankCheckpoint,
    setShowBindBank,
    setBindBankModal,
    setBindBankResolver,
    resolveBindBank,
    setErrorMessage,
    setErrorModal,
    setAnimationInitData,
    setJackpotList,
    setTurnoverLimitTip,
    setWithdrawRule,
    setPromotion,
    setBrowserAgent,
    setNavigator,
    setTrackLocation,
    setStorage,
    setCustomrerServiceModal,
    setPlayingGame,
    setJackpotWinning,
    setToast,
    pushToast,
    setGameSearchPageEnable,
    setConnectSocket,
    setTelegramUserData,
    setUpdateMessage,
    setNormalBroadcast,
  }
})
